import React, { useContext, useState } from 'react';
import Select, { components as selectComponents } from 'react-select';
import PropTypes from 'prop-types';
import useSchools from '../../hooks/useSchools';
import { menuSurface, menuListSurface } from '../../utils/selectMenuStyles';
import styles from '../HomePageForm/HomePageForm.module.sass';
import { CustomerContext } from '../../contexts/CustomerContext';

// `hideLabel`   — omit the built-in "School Name" label (so a caller can
//                 supply its own label alongside a required-asterisk / error).
// `error`       — apply a red control border to signal a validation failure.
// `noDefault`   — start empty (no defaultValue from customer.currentSchool)
//                 so the user has to actively pick a school; pair with
//                 clearing the context value on mount if you don't want a
//                 stored school to submit silently.
// `placeholder` — react-select empty-state text (e.g. "Type your school name").
// `openOnTypeOnly` — keep the menu shut until at least one character is typed,
//                 instead of dumping the whole school list on click. There are
//                 ~13k schools and react-select renders every filtered option
//                 (no virtualization), so the click-to-open list is both
//                 unusable and a lot of DOM. Opt-in: AnotherStudentForm still
//                 opens on click.
// A magnifying glass in place of react-select's chevron, for the type-to-search
// mode. The field is a combobox that only responds to typing, and with the
// chevron hidden it read as an ordinary text box -- people filled it in and
// looked for a Continue button instead of picking from the list. A search glyph
// says "type and choose" without a word of instruction.
//
// Not a chevron and not an arrow: a chevron promises a list on click, which
// openMenuOnClick={false} does not deliver, and an arrow reads as submit.
//
// aria-hidden because react-select already exposes the combobox semantics; the
// icon is a visual cue, not a second thing to announce.
const SearchIndicator = (props) => (
  <selectComponents.DropdownIndicator {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <circle cx="11" cy="11" r="7" />
      <line x1="20" y1="20" x2="16.65" y2="16.65" />
    </svg>
  </selectComponents.DropdownIndicator>
);

const SchoolInput = ({
  onChange, hideLabel = false, error = false, noDefault = false, placeholder,
  openOnTypeOnly = false,
}) => {
  const schools = useSchools();
  const { customer } = useContext(CustomerContext);
  // Only consulted when openOnTypeOnly is set; it drives menuIsOpen.
  const [typed, setTyped] = useState('');

  // Match the shared form input dimensions so the School dropdown reads as
  // the same species of control as the Email + Access-code fields
  // (48px tall, 1px #e2e8f0 border, 10px radius, 14px horizontal padding,
  // 16px font). Focus + error states also mirror the parent SCSS.
  const controlBase = {
    minHeight: '48px',
    borderRadius: '10px',
    borderColor: '#e2e8f0',
    fontSize: '16px',
  };
  const customStyles = {
    // Selected = light brand tint; focused/hover = soft grey. Match GradeInput.
    option: (provided, state) => ({
      ...provided,
      color: '#0f172a',
      backgroundColor: state.isSelected
        ? '#e0f2fe'
        : state.isFocused
          ? '#f1f5f9'
          : 'white',
      fontFamily: 'inherit',
      cursor: 'pointer',
      ':active': { backgroundColor: '#e0f2fe' },
    }),
    // Rounded to match the control and the rest of the form's inputs.
    menu: menuSurface,
    menuList: menuListSurface,
    singleValue: (provided) => ({
      ...provided,
      color: '#0f172a',
      fontFamily: 'inherit',
      fontSize: '16px',
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#94a3b8',
      fontSize: '16px',
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: '0 12px',
    }),
    indicatorSeparator: () => ({ display: 'none' }),
    // In openOnTypeOnly mode the chevron is replaced by a search glyph (see
    // SearchIndicator). Muted to match the placeholder so it reads as a hint
    // rather than a control, and darkening on focus keeps it alive with the
    // field. Outside that mode react-select's own chevron is left alone.
    ...(openOnTypeOnly
      ? {
        dropdownIndicator: (provided, state) => ({
          ...provided,
          color: state.isFocused ? '#64748b' : '#94a3b8',
          paddingRight: '14px',
          ':hover': { color: '#64748b' },
        }),
      }
      : {}),
    // Focus ring matches the shared form input style: brand-blue border +
    // subtle translucent brand halo. Error state overrides both to red.
    control: (provided, state) => {
      const base = { ...provided, ...controlBase };
      if (error) {
        return {
          ...base,
          borderColor: '#dc2626',
          boxShadow: '0 0 0 3px rgba(220, 38, 38, 0.15)',
          '&:hover': { borderColor: '#dc2626' },
        };
      }
      if (state.isFocused) {
        return {
          ...base,
          borderColor: '#008fcf',
          boxShadow: '0 0 0 3px rgba(0, 143, 207, 0.18)',
          '&:hover': { borderColor: '#008fcf' },
        };
      }
      return { ...base, boxShadow: 'none', '&:hover': { borderColor: '#cbd5e1' } };
    },
    // Kill react-select's internal input styling so the parent form's
    // `.field input:focus { box-shadow: ... }` glow doesn't get inherited
    // and paint a light-blue pill around the letters as the user types.
    // The parent rule is a regular CSS class, so emotion inline styles win
    // only when they carry !important on box-shadow.
    input: (provided) => ({
      ...provided,
      background: 'transparent',
      boxShadow: 'none !important',
      color: '#0f172a',
    }),
  };

  const preselect = !noDefault && customer.currentSchool
    ? { value: customer.currentSchool.id, label: customer.currentSchool.name }
    : '';

  const select = (
    <Select
      id="school-input"
      defaultValue={preselect}
      options={schools.map((selectSchool) => (
        ({ value: selectSchool.id, label: selectSchool.name })
      ))}
      placeholder={placeholder}
      className={styles.schoolInput}
      styles={customStyles}
      onChange={onChange}
      // Clicking must not open the list; typing must. `undefined` hands
      // menuIsOpen back to react-select's own state for the default behaviour.
      openMenuOnClick={!openOnTypeOnly}
      menuIsOpen={openOnTypeOnly ? typed.trim().length > 0 : undefined}
      components={openOnTypeOnly ? { DropdownIndicator: SearchIndicator } : undefined}
      onInputChange={(value, { action }) => {
        if (!openOnTypeOnly) return;
        // 'input-change' is the user typing. Every other action that ends the
        // interaction (picking an option, blurring, closing) has to clear the
        // buffer, or menuIsOpen stays true and the list hangs open over the
        // fields below it.
        if (action === 'input-change') setTyped(value);
        else setTyped('');
      }}
    />
  );

  if (hideLabel) return select;

  return (
    <label htmlFor="school-input" className={styles.label}>
      School Name
      {select}
    </label>
  );
};

SchoolInput.propTypes = {
  onChange: PropTypes.func.isRequired,
  hideLabel: PropTypes.bool,
  error: PropTypes.bool,
  noDefault: PropTypes.bool,
  placeholder: PropTypes.string,
  openOnTypeOnly: PropTypes.bool,
};

export default SchoolInput;
